<template>
  <div>
    <div class="r-form__section-heading m-l-15 m-r-15 m-b-20">{{ $t('TRACKING_SETTINGS_TITLE') }}</div>
    <div class="row">
      <Field
        :label="$t('COUNT_ATTEMPTS')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('COUNT_ATTEMPTS_TT')"
      >
        <template #input>
          <v-select
            v-model="countAttempsSelected"
            :clearable="false"
            :searchable="false"
            :options="countAttempsOnSelectOptions"
          />
        </template>
      </Field>
      <Field
        :label="$t('TRACK_CONVERSION_BY')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('TRACK_CONVERSION_BY_TT')"
      >
        <template #input>
          <v-select
            v-model="trackConversionBySelected"
            :clearable="false"
            :searchable="false"
            :options="trackConversionBySelectOptions"
          />
        </template>
      </Field>
    </div>
    <div class="r-form__section-heading m-l-15 m-r-15">{{ $t('CALL_FILTER_SETTINGS_TITLE') }}</div>
    <div class="row">
      <Field
        :label="$t('MINIMUM_NUMBER_OF_CALLS')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('MINIMUM_NUMBER_OF_CALLS_TT')"
      >
        <template #input>
          <ValidationProvider slim>
            <input
              v-model="erpcSetting.minimumNumberOfCalls"
              type="number"
              placeholder="15"
              class="form-control"
              @change="onChange"
            />
          </ValidationProvider>
        </template>
      </Field>
      <Field
        :label="$t('DESIRED_NUMBER_OF_CALLS')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('DESIRED_NUMBER_OF_CALLS_TT')"
      >
        <template #input>
          <ValidationProvider slim>
            <input
              v-model="erpcSetting.desiredNumberOfCalls"
              type="number"
              placeholder="75"
              class="form-control"
              @change="onChange"
            />
          </ValidationProvider>
        </template>
      </Field>
      <Field
        :label="$t('MINIMUM_NUMBER_OF_HOURS')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('MINIMUM_NUMBER_OF_HOURS_TT')"
      >
        <template #input>
          <ValidationProvider slim>
            <input
              v-model="erpcSetting.minimumNumberOfHours"
              type="number"
              placeholder=""
              class="form-control"
            />
          </ValidationProvider>
        </template>
      </Field>
      <Field
        :label="$t('DESIRED_NUMBER_OF_HOURS')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('DESIRED_NUMBER_OF_HOURS_TT')"
      >
        <template #input>
          <ValidationProvider slim>
            <input
              v-model="erpcSetting.desiredNumberOfHours"
              type="number"
              placeholder="720"
              class="form-control"
              @change="onChange"
            />
          </ValidationProvider>
        </template>
      </Field>
      <Field
        :label="$t('SKIP_LATEST_HOURS')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('SKIP_LATEST_HOURS_TT')"
      >
        <template #input>
          <ValidationProvider slim>
            <input
              v-model="erpcSetting.skipLatestHours"
              type="number"
              placeholder=""
              class="form-control"
            />
          </ValidationProvider>
        </template>
      </Field>
    </div>
    <div class="r-form__section-heading m-l-15 m-r-15">{{ $t('PRIORITY_SETTINGS_TITLE') }}</div>
    <div class="row">
      <Field
        :label="$t('NEW_TARGETS_PRIORITY')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('NEW_TARGETS_PRIORITY_TT')"
      >
        <template #input>
          <ValidationProvider
            v-slot="{ classes }"
            ref="prNewTargetPriority"
            rules="min_value:0"
            slim
          >
            <input
              v-model="erpcSetting.newTargetPriority"
              name="prNewTargetPriority"
              type="number"
              placeholder="55"
              :class="classes"
              class="form-control"
              @change="onChange"
            />
          </ValidationProvider>
        </template>
      </Field>
      <Field
        :label="$t('UNDER_PERFORMING_TARGETS_PRIORITY')"
        class="col-sm-6"
        :has-tooltip="true"
        :tooltip-text="$t('UNDER_PERFORMING_TARGETS_PRIORITY_TT')"
      >
        <template #input>
          <ValidationProvider 
            v-slot="{ classes }"
            ref="prUnderPerformingTargetPriority"
            rules="min_value:0"
            slim
          >
            <input
              v-model="erpcSetting.underPerformingTargetPriority"
              name="prUnderPerformingTargetPriority"
              type="number"
              placeholder="10"
              :class="classes"
              class="form-control"
              @change="onChange"
            />
          </ValidationProvider>
        </template>
      </Field>
    </div>
    <template v-if="hasRpcLegacy">
      <div class="r-form__section-heading m-l-15 m-r-15">{{ $t('LEGACY_SETTINGS_TITLE') }} <i v-tooltip.right="$t('LEGACY_SETTINGS_TITLE_TT')" class="fa fa-question-circle m-l-5" /></div>
      <div class="row">
        <Field
          :label="$t('DEFAULT_RATIO')"
          class="col-sm-4"
          :has-tooltip="true"
          :tooltip-text="$t('DEFAULT_RATIO_TT')"
        >
          <template #input>
            <ValidationProvider slim>
              <input
                v-model="erpcSetting.defaultRatio"
                type="number"
                class="form-control"
                @blur="onChange"
                @change="onChange"
              />
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('OVERRIDE_RATIO')"
          class="col-sm-4"
          :has-tooltip="true"
          :tooltip-text="$t('OVERRIDE_RATIO_TT')"
        >
          <template #input>
            <ValidationProvider slim>
              <input
                v-model="erpcSetting.overrideRatio"
                type="number"
                placeholder=""
                class="form-control"
              />
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('OVERRIDE_AMOUNT')"
          class="col-sm-4"
          :has-tooltip="true"
          :tooltip-text="$t('OVERRIDE_AMOUNT_TT')"
        >
          <template #input>
            <ValidationProvider slim>
              <span class="currency">$</span>
              <input
                v-model="erpcSetting.overrideAmount"
                type="number"
                placeholder=""
                class="form-control p-l-20"
              />
            </ValidationProvider>
          </template>
        </Field>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import Field from '@/core/components/Form/BaseFormFieldV2';

export default {
  name: 'ERPCSettingsAdvanced',
  components: {
    Field
  },
  props: {
    erpcSetting: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  computed: {
    ...mapState({
      flags: 'currentAccountFeatureFlags'
    }),
    countAttempsOnSelectOptions() {
      return [
        { label: this.$t('ON_DIAL'), id: 'DIAL' },
        { label: this.$t('ON_CONNECT'), id: 'CONNECT' },
      ];
    },
    trackConversionBySelectOptions() {
      return [
        { label: this.$t('BUYER'), id: 'BUYER' },
        { label: this.$t('TARGET'), id: 'TARGET' },
        { label: this.$t('CAMPAIGN_AND_BUYER'), id: 'CAMPAIGN_BUYER' },
        { label: this.$t('CAMPAIGN_AND_TARGET'), id: 'CAMPAIGN_TARGET' },
        { label: this.$t('PUBLISHER_CAMPAIGN_BUYER'), id: 'PUBLISHER_CAMPAIGN_BUYER' },
        { label: this.$t('PUBLISHER_CAMPAIGN_TARGET'), id: 'PUBLISHER_CAMPAIGN_TARGET' }
      ];
    },
    countAttempsSelected: {
      get() {
        return this.countAttempsOnSelectOptions.find( ({ id }) => id === this.erpcSetting?.countAttempsOn );
      },
      set(value) {
        this.erpcSetting.countAttempsOn = value.id;
      }
    },
    trackConversionBySelected: {
      get() {
        return this.trackConversionBySelectOptions.find( ({ id }) => id === this.erpcSetting?.trackConversionBy );
      },
      set(value) {
        this.erpcSetting.trackConversionBy = value.id;
      }
    },
    hasRpcLegacy() {
      return !!this.flags.rpc_legacy;
    },
  },
  methods: {
    onChange() {
      this.erpcSetting.minimumNumberOfCalls = this.erpcSetting.minimumNumberOfCalls == '' ? 15 : this.sanitizeNumber(this.erpcSetting.minimumNumberOfCalls);
      this.erpcSetting.desiredNumberOfCalls = this.erpcSetting.desiredNumberOfCalls == '' ? 75 : this.sanitizeNumber(this.erpcSetting.desiredNumberOfCalls);
      this.erpcSetting.desiredNumberOfHours = this.erpcSetting.desiredNumberOfHours == '' ? 720 : this.sanitizeNumber(this.erpcSetting.desiredNumberOfHours);
      this.erpcSetting.newTargetPriority = this.erpcSetting.newTargetPriority == '' ? 55 : this.sanitizeNumber(this.erpcSetting.newTargetPriority);
      this.erpcSetting.underPerformingTargetPriority = this.erpcSetting.underPerformingTargetPriority == '' ? 10 : this.sanitizeNumber(this.erpcSetting.underPerformingTargetPriority);
    },
    sanitizeNumber(value) {
      return value.toString().replace(/[+-]/g, '');
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>